import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Award, Zap, Shield, BadgeCheck, Star } from 'lucide-react';
import { heroSlides, brands, whyChooseUs, testimonials } from '../mockData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

  const iconMap = {
    Award: Award,
    Zap: Zap,
    Shield: Shield,
    BadgeCheck: BadgeCheck
  };

  return (
    <div className="min-h-screen">
      {/* Hero Carousel */}
      <section className="relative h-[600px] overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-all duration-700 ${
              index === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
            }`}
          >
            <div className="container mx-auto px-4 h-full">
              <div className="grid md:grid-cols-2 gap-8 items-center h-full">
                {/* Text Content */}
                <div className="space-y-6">
                  <div className="inline-block">
                    <div className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      {slide.highlight}
                    </div>
                  </div>
                  <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
                    {slide.title}
                    <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-600 mt-2">
                      {slide.subtitle}
                    </span>
                  </h1>
                  <p className="text-xl text-gray-600">{slide.description}</p>
                  <p className="text-lg text-gray-500">{slide.highlightText}</p>
                  <div className="flex gap-4 pt-4">
                    <Link to="/kontakt">
                      <Button className="bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-700 hover:to-cyan-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
                        Kontakt oss
                      </Button>
                    </Link>
                    <Link to="/priser/mobil">
                      <Button variant="outline" className="border-2 border-sky-600 text-sky-600 hover:bg-sky-50 px-8 py-6 text-lg rounded-xl transition-all">
                        Se priser
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* Image */}
                <div className="relative h-full flex items-center justify-center">
                  <div className="relative w-full h-[400px] rounded-2xl overflow-hidden shadow-2xl">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110"
        >
          <ChevronLeft size={24} className="text-gray-900" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg transition-all hover:scale-110"
        >
          <ChevronRight size={24} className="text-gray-900" />
        </button>

        {/* Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide ? 'bg-sky-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Velkommen til en rask og trygg reparasjon!
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Hos oss kan du komme med alle typer problemer på enheten din, enten det gjelder knust skjerm, 
            dårlig batteri eller andre feil – når det passer best for deg! Våre dyktige teknikere står 
            klare til å hjelpe deg med dine enheter.
          </p>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Vi reparerer alle merker
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {brands.map((brand, index) => {
              // Define links for brands with pricing pages
              const brandLinks = {
                'Apple': '/priser/mobil/apple',
                'Samsung': '/priser/mobil/samsung',
                'Huawei': '/priser/mobil/huawei'
              };
              
              const brandLink = brandLinks[brand.name];
              
              const cardContent = (
                <CardContent className="p-8 text-center">
                  <div className="text-5xl mb-3">{brand.logo}</div>
                  <div className="font-semibold text-gray-900">{brand.name}</div>
                </CardContent>
              );
              
              if (brandLink) {
                return (
                  <Link key={index} to={brandLink}>
                    <Card className="hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer border-2 hover:border-sky-200">
                      {cardContent}
                    </Card>
                  </Link>
                );
              }
              
              return (
                <Card 
                  key={index} 
                  className="hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer border-2 hover:border-sky-200"
                >
                  {cardContent}
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Hvorfor velge oss
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={index} className="text-center hover:shadow-xl transition-all hover:-translate-y-2 border-2 hover:border-sky-200">
                  <CardContent className="p-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-sky-100 to-cyan-100 rounded-2xl mb-4">
                      <Icon size={32} className="text-sky-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-12">
            Hva kundene sier
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-4 italic">"{testimonial.comment}"</p>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.date}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-sky-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Trenger du hjelp med enheten din?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Kontakt oss i dag for en gratis konsultasjon!
          </p>
          <Link to="/kontakt">
            <Button className="bg-white text-sky-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all">
              Kontakt oss nå
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
